import type {
  ApiUsageView,
  ChannelsView,
  ConnectionClearResponse,
  ConnectionSetResponse,
  ConnectionSlot,
  ConnectionsResponse,
  ConnectionTestResponse,
  CustomersView,
  ErrorCode,
  IsoDate,
  McpAddressCreated,
  McpAddressList,
  McpAddressRevoked,
  OrganizationView,
  OzetPeriod,
  OzetSummaryView,
  OzetView,
  PersonActivity,
  RejectReasonCode,
  RequestRejectCode,
  SalesMapCategoryFilter,
  SalesMapPeriod,
  SalesMapView,
  SeoReportView,
  SiteChatList,
  SiteStatsView,
  VoiceJobBody,
  VoiceJobCreated,
  VoiceView,
} from '@twin/contracts';
import { ERROR_MESSAGES_TR, isErrorCode, SNAPSHOT_REFRESH_CODES } from '@twin/contracts';
import type { EvalView } from './evals.js';
import type { KbView } from './kb.js';
import type { Basemap, CellDetail, LeadDetail, LeadsView } from './leads.js';
import type {
  FitLabelInput,
  LearningBatchInput,
  LearningBatchResult,
  LearningView,
  RevertInput,
  RevertResult,
} from './learning.js';
import type { LogQuery, LogView } from './log.js';
import type {
  OutreachCampaignApproveResponse,
  OutreachMessageDecisionRequest,
  OutreachMessageDecisionResponse,
  OutreachRefutationResponse,
  OutreachView,
} from './outreach.js';
import type { OutreachLog, OutreachLogText } from './outreach-log.js';
import type { ViolationCode } from './policy-rules.js';
import type { ProductsView } from './products.js';
import type { ProspectDetail, ProspectsView } from './prospects.js';
import type { ReplyExamplesView } from './reply-examples.js';
import type { SekreterItemInput, SekreterView } from './sekreter.js';
import type { AuditEntry, PolicyChangeInput, PreferencesPatch, Settings } from './settings.js';
import type { ShopView } from './shop.js';
import type { AgentId, ApprovalTicket, ApproveInput, HoldTicket, Instant, Snapshot } from './types.js';

/**
 * Everything the panel asks of the Twin API (specs/c2-kontratlar.md 2.10). The panel never
 * acts externally itself: approve only records the decision, and the executor applies it
 * after the undo window, or not at all when undo reaches it first. Two implementations:
 * the HTTP client (./http.ts) and the in-memory mock server (./mock/server.ts).
 */
export interface TwinApi {
  getSnapshot(): Promise<Snapshot>;
  /**
   * T4 (every tier other than T3): asked the moment the hold starts. The server records the
   * panel approval view and returns a signed token that approve must carry, sent no earlier
   * than sendNotBeforeLocal (local performance clock, 2.6 step 3).
   */
  startHold(proposalId: string, payloadHash: string): Promise<HoldTicket>;
  approve(proposalId: string, input: ApproveInput): Promise<ApprovalTicket>;
  /**
   * Cancels a held action inside its undo window. The proposal comes back to the queue
   * under a new id (null when a newer proposal for the same subject is already queued).
   * Rejects with AlreadyAppliedError when the window has passed or the executor started.
   */
  undo(ticketId: string): Promise<{ requeuedProposalId: string | null }>;
  reject(proposalId: string, reason: RejectReasonCode, note: string, payloadHash: string): Promise<void>;
  /** Sercan confirms that he did the manual step (sent the copied reply, made the refund) himself. */
  markSent(proposalId: string): Promise<void>;
  /** Gönderilecekler (v0.1.54): Gönder on an approved e-mail reply; the worker sends it through Gmail. */
  sendReply(approvalId: string): Promise<void>;
  /** Eski hâline dön: a new listing_revert approval with its own undo window. */
  revert(doneId: string): Promise<ApprovalTicket>;
  answerRequest(id: string, input: { text: string; addToKb: boolean }): Promise<void>;
  acceptRequest(id: string, input: { targetDate: IsoDate; note: string }): Promise<void>;
  /** Bilgi bankası: every entry with its products; drafts wait for Sercan. */
  getKb(): Promise<KbView>;
  /** Approves a draft with the text as Sercan left it (only the changed fields are sent). */
  approveKb(id: string, input: { answerEn?: string; answerTr?: string }): Promise<void>;
  retireKb(id: string): Promise<void>;
  /** Sekreter: the last Telegram list and Sercan's own items (v0.1.28). */
  getSekreter(): Promise<SekreterView>;
  addSekreterItem(input: SekreterItemInput): Promise<void>;
  doneSekreterItem(id: string): Promise<void>;
  reopenSekreterItem(id: string): Promise<void>;
  /** Geçmiş cevaplar (v0.1.38): the shop's own past replies; retire takes one out of the Temsilci's examples. */
  getReplyExamples(): Promise<ReplyExamplesView>;
  /** Değerlendirme (v0.1.260): the Temsilci's reply evaluation. */
  getEvals(): Promise<EvalView>;
  retireReplyExample(id: string): Promise<void>;
  restoreReplyExample(id: string): Promise<void>;
  deferRequest(id: string, until: IsoDate): Promise<void>;
  rejectRequest(id: string, reason: RequestRejectCode, note: string): Promise<void>;
  /** Soft stop: the agent finishes its current step. */
  pauseAgent(id: AgentId): Promise<void>;
  resumeAgent(id: AgentId): Promise<void>;
  /** Soft stop for the whole team: current steps finish, queue kept, executor stops. */
  pauseTeam(): Promise<void>;
  /**
   * Hard stop: running steps are cancelled, the executor stops, approvals inside their undo
   * window are undone and come back to the queue under new ids.
   */
  emergencyStop(): Promise<{ stoppedAt: Instant; requeued: { from: string; to: string }[] }>;
  /**
   * Going back to running after a stop loosens the system, so it needs a hold like a T4
   * approval: the token is asked when the hold starts (POST /system/kill-switch/hold).
   */
  startResumeHold(): Promise<HoldTicket>;
  /**
   * Back to running; returns how many approved actions the executor will now apply. From a
   * stop the hold token is required (403 hold_required without it); from a pause it is null.
   */
  resumeTeam(holdToken: string | null): Promise<{ pendingExecutions: number }>;

  // ---- Ayarlar ----
  /** Settings read model: connections, policy, hard limits, preferences. Never contains a credential. */
  getSettings(): Promise<Settings>;
  /**
   * Changes a policy value, feature flag, stage or agent switch and returns the audit entry.
   * The server re-checks the rules: loosening without a reason and the completed hold, or a
   * change that breaks a structural rule (bounds, prerequisites, team cap), is refused.
   */
  changePolicy(input: PolicyChangeInput): Promise<AuditEntry>;
  /** Revokes every grant of a connection after the 2 s hold; the executor deletes the token. */
  revokeConnection(id: string, input: { confirmedByHold: boolean }): Promise<AuditEntry>;
  /** Free preferences: one tap, no reason, not audited. */
  updatePreferences(patch: PreferencesPatch): Promise<void>;
  /** Ayarlar › API kullanımı: every outside API with today, the month, what is left and 30 days (v0.1.164). */
  getApiUsage(): Promise<ApiUsageView>;
  /** Site: the last 30 days of cadgaragepro.com from the four sources and their states (v0.1.166). */
  getSiteStats(): Promise<SiteStatsView>;
  /** Kanallar (28 Oct 2026). */
  getChannels(): Promise<ChannelsView>;
  /** Satış haritası (v0.1.269): orders by country and town for a period and a product family. */
  getSalesMap(period: SalesMapPeriod, category: SalesMapCategoryFilter): Promise<SalesMapView>;
  /** Seslendirme (v0.1.255): the newest voice jobs with their parts, and the voices Genesis lists. */
  getVoice(): Promise<VoiceView>;
  /** Seslendirme (v0.1.255): a new job; the worker voices it within five minutes. */
  createVoiceJob(body: VoiceJobBody): Promise<VoiceJobCreated>;
  /** Site sohbetleri (v0.1.248): the site assistant's chats, newest first; before pages back by start time. */
  getSiteChats(before?: string): Promise<SiteChatList>;
  /** SEO ve yapay zekâ: the AI check's questions by week and the search words of Google and Bing (v0.1.190). */
  getSeoReport(): Promise<SeoReportView>;
  /** GET /ozet (v0.1.175). */
  getOzet(): Promise<OzetView>;
  /** GET /ozet/summary?period= (v0.1.257): one period against the one before, day by day and week by week. */
  getOzetSummary(period: OzetPeriod): Promise<OzetSummaryView>;
  /** Ayarlar › Güvenlik › MCP adresleri (v0.1.215): never the token. */
  listMcpAddresses(): Promise<McpAddressList>;
  /** A new MCP address; the answer carries the address once. */
  createMcpAddress(label: string): Promise<McpAddressCreated>;
  revokeMcpAddress(id: string): Promise<McpAddressRevoked>;

  // ---- Bağlantı değerleri (0.4.1) ----
  /** Status, last4 and the last test per slot; never a value. */
  getConnections(): Promise<ConnectionsResponse>;
  /** Replaces the value of a slot. The server checks the shape; the value is never shown again. */
  setConnection(slot: ConnectionSlot, value: string): Promise<ConnectionSetResponse>;
  clearConnection(slot: ConnectionSlot): Promise<ConnectionClearResponse>;
  /** A server side test where one exists (the worker runs it; the panel polls getConnections). */
  testConnection(slot: ConnectionSlot): Promise<ConnectionTestResponse>;

  // ---- Leadler ----
  /** Harita and Huni: scan cells and the lead funnel (growth.v_cell_map, leadFunnel). */
  getLeads(): Promise<LeadsView>;
  /** One company card (GET /leads/{leadId}): fit split, the pilot's notes, evidence, stage history. */
  getLeadDetail(leadId: string): Promise<LeadDetail>;
  /** One cell card (GET /leads/cells/{cellId}): the cell, its runs, its queries and its companies. */
  getCellDetail(cellId: string): Promise<CellDetail>;
  /** The basemap layers of a region (GET /leads/basemap/{region}): water, coastline, main roads, industrial areas. */
  getBasemap(region: string): Promise<Basemap>;
  /** Potansiyel müşteriler (GET /leads/prospects): every lead with how it was found, kind, production, decision, contact; summary, costs, jobs. */
  getProspects(): Promise<ProspectsView>;
  /** One company of Potansiyel müşteriler (GET /leads/prospects/{leadId}): every search, evidence, decisions, channels, mails. */
  getProspect(leadId: string): Promise<ProspectDetail>;
  /** Tanıtım gönderimi (GET /leads/outreach, v0.1.135): the conditions of each campaign and the first wave. */
  getOutreach(): Promise<OutreachView>;
  /** Tanıtım takibi (GET /leads/outreach/log, v0.1.169): every mail that went out, the first mails that wait, the measures. */
  getOutreachLog(): Promise<OutreachLog>;
  /** v0.1.170: one mail's text in English and Turkish, and the latest answer, when its row is opened. */
  getOutreachLogText(messageId: string): Promise<OutreachLogText>;
  /** v0.1.137: the campaign approval after the hold (the client takes the hold token itself). */
  approveOutreachCampaign(code: string): Promise<OutreachCampaignApproveResponse>;
  /** v0.1.137: Onayla or Reddet on one wave mail. */
  decideOutreachMessage(
    messageId: string,
    input: OutreachMessageDecisionRequest,
  ): Promise<OutreachMessageDecisionResponse>;
  /** v0.1.136: the refutation step on one finding of the quality audit. */
  refuteOutreachAudit(
    auditId: string,
    itemNo: number,
    decision: 'upheld' | 'overturned',
  ): Promise<OutreachRefutationResponse>;

  // ---- Mağaza ----
  /** Satışlar and İlanlar: listings with 30 and 90 day numbers, the last orders, countries, twelve months. */
  getShop(): Promise<ShopView>;
  /** Kontrol listesi B1: asks the worker for an Etsy read now (picked up within five minutes). */
  requestShopSync(): Promise<{ queued: boolean }>;
  /** Ürünler: every product card with its files, geometry, listing and images (GET /shop/products). */
  getProducts(): Promise<ProductsView>;
  /** Müşteriler: who wrote and who bought, per source and per customer (GET /customers). */
  getCustomers(): Promise<CustomersView>;
  /** Kişi: what one agent or human did in the last days (GET /people/{id}/activity). */
  getPersonActivity(id: string, days?: number): Promise<PersonActivity>;
  /** Ekip › Organizasyon: the company in departments (GET /team/organization, v0.1.150). */
  getOrganization(): Promise<OrganizationView>;

  // ---- Kayıtlar ----
  /** The audit chain as Turkish sentences, newest first; since (epoch ms) answers only newer events. */
  getLog(query: LogQuery): Promise<LogView>;
  /** Öğrenme: the learning-weekly view model, company-level only, no buyer data. */
  getLearning(): Promise<LearningView>;
  /**
   * Geri al for an automatic (A1, A2) change. The server refuses suppression and opt-out
   * phrases (append-only), a revert without a reason, and a loosening revert without the hold.
   */
  revertLearningChange(input: RevertInput): Promise<RevertResult>;
  /**
   * One decision on the whole learning batch (twin.approval_batches kind learning_batch).
   * Approving needs the reason and the completed hold, an H3 item its own hold too; H2
   * items are never approved here (they ship through their release PR).
   */
  decideLearningBatch(input: LearningBatchInput): Promise<LearningBatchResult>;
  /** A fit label from Sercan (growth.fit_labels, label_source sercan). */
  labelFit(input: FitLabelInput): Promise<void>;
}

/**
 * A refusal from the API with its error code (2.7). The message is the server's Turkish
 * text, safe to show; code is kept as a string because a newer server may add codes.
 */
export class TwinApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | null;
  constructor(code: string, message: string, status = 0, requestId: string | null = null) {
    super(message);
    this.name = 'TwinApiError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }

  /** A known contract code, or null for one this panel does not know yet. */
  get knownCode(): ErrorCode | null {
    return isErrorCode(this.code) ? this.code : null;
  }
}

/** Thrown by undo when the executor already applied the action (409 already_applied). */
export class AlreadyAppliedError extends TwinApiError {
  constructor(ticketId: string, requestId: string | null = null) {
    super('already_applied', `ticket ${ticketId} was already applied`, 409, requestId);
    this.name = 'AlreadyAppliedError';
  }
}

/** Thrown by the settings API when the server refuses a change. */
export class PolicyRejectedError extends Error {
  readonly code: ViolationCode | 'not_connected';
  constructor(code: ViolationCode | 'not_connected', message: string) {
    super(message);
    this.name = 'PolicyRejectedError';
    this.code = code;
  }
}

/**
 * No answer (network, timeout) or an answer that is not the contract's shape. The request
 * may still have been recorded, so the panel reads the state again instead of assuming.
 */
export class TransportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransportError';
  }
}

/**
 * True when the panel should reload the snapshot before deciding again: the codes of 2.7
 * that mean the screen was out of date, the kill switch, and every answer that leaves the
 * outcome unknown (no answer, a 5xx): a write may have landed, so read before retrying.
 */
export function needsSnapshotRefresh(error: unknown): boolean {
  if (error instanceof TransportError) return true;
  if (!(error instanceof TwinApiError)) return false;
  return (
    (SNAPSHOT_REFRESH_CODES as readonly string[]).includes(error.code) ||
    error.code === 'kill_switch_engaged' ||
    error.status >= 500
  );
}

/**
 * Short Turkish text for a failed decision: the code's own message for a known refusal, the
 * connection hint only when the request never got an answer.
 */
export function decisionErrorText(error: unknown, fallback: string): string {
  if (!(error instanceof TwinApiError)) return fallback;
  const known = error.knownCode;
  if (known) return ERROR_MESSAGES_TR[known];
  return error.message.trim() === '' ? fallback : error.message;
}
